/**
 * A porta do convite de teste: `/convidar/<codigo>`.
 *
 * O administrador gera um convite para **um** módulo — miolos ou documentos — e
 * quem abrir o link, já logado, ativa aquele módulo na hora. Quem não estiver
 * logado é levado para o login, e o convite vale depois de entrar.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Check, Loader2, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';

const ROTULO_MODULO: Record<'MIOLOS' | 'DOCUMENTOS', string> = {
  MIOLOS: 'Miolos (agenda)',
  DOCUMENTOS: 'Documentos (editor acadêmico)',
};

export function Convite() {
  const { codigo = '' } = useParams();
  const { usuario, recarregar } = useAuth();
  const navegar = useNavigate();

  const [estado, setEstado] = useState<'carregando' | 'pronto' | 'erro' | 'usado'>('carregando');
  const [modulo, setModulo] = useState<'MIOLOS' | 'DOCUMENTOS' | null>(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!usuario || !codigo) return;
    void (async () => {
      try {
        const { modulo: liberado } = await api.usarConvite(codigo);
        setModulo(liberado);
        setEstado('pronto');
        await recarregar();
      } catch (causa) {
        setErro(causa instanceof Error ? causa.message : 'Não foi possível usar o convite.');
        setEstado('erro');
      }
    })();
  }, [usuario, codigo, recarregar]);

  if (!usuario) {
    return (
      <div className="mx-auto max-w-md space-y-4 py-16">
        <Card>
          <CardHeader>
            <CardTitle>Convite de teste</CardTitle>
            <CardDescription>Entre na sua conta para ativar o módulo do convite.</CardDescription>
          </CardHeader>
          <CardContent className="flex items-center gap-2">
            <Button asChild>
              <Link to={`/entrar?convite=${codigo}`}>Entrar</Link>
            </Button>
            <Button asChild variant="contorno">
              <Link to="/criar-conta">Criar conta</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-4 py-16">
      <Card>
        <CardHeader>
          <CardTitle>Convite de teste</CardTitle>
          <CardDescription>O módulo liberado pelo convite.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {estado === 'carregando' ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Ativando...
            </p>
          ) : null}
          {estado === 'pronto' && modulo ? (
            <>
              <p className="flex items-center gap-2 text-sm text-success">
                <Check className="size-4" />
                Módulo ativado: <strong>{ROTULO_MODULO[modulo]}</strong>
              </p>
              <Button onClick={() => navegar(modulo === 'MIOLOS' ? '/criar' : '/documentos')}>
                Abrir {ROTULO_MODULO[modulo]}
                <ArrowRight />
              </Button>
            </>
          ) : null}
          {estado === 'erro' ? (
            <p className="flex items-center gap-2 text-sm text-destructive">
              <TriangleAlert className="size-4" />
              {erro}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
